/**
 * Pure rekenhulpjes voor Medewerkers, HR-dashboard en Leveranciers (26 sep 2026).
 * Los van de schermen zodat ze te testen zijn.
 */

/** multipleSelects kan als array óf komma-string binnenkomen — altijd een array. */
export const alsLijst = (w) => {
  if (Array.isArray(w)) return w.filter(Boolean).map(String);
  if (typeof w === 'string' && w) return w.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
};

/** Galerij-filter: functie voor iedereen; magazijnsleutel en rijbewijs alleen als de server
 *  die velden meestuurt (HR-recht). */
export function filterGalerij(personen, { functie = '', magazijnsleutel = '', rijbewijs = '', zoek = '' } = {}) {
  const z = zoek.trim().toLowerCase();
  return (personen || []).filter((p) => {
    if (z && !(p.naam || '').toLowerCase().includes(z)) return false;
    if (functie && !alsLijst(p.functies).includes(functie)) return false;
    if (magazijnsleutel === 'ja' && !p.magazijnsleutel) return false;
    if (magazijnsleutel === 'nee' && p.magazijnsleutel) return false;
    if (rijbewijs === 'ja' && !['Schakel', 'Automaat'].includes(p.rijbewijsB)) return false;
    if (rijbewijs === 'nee' && ['Schakel', 'Automaat'].includes(p.rijbewijsB)) return false;
    return true;
  });
}

export const alleFuncties = (items) =>
  [...new Set((items || []).flatMap((i) => alsLijst(i.functies)))].sort((a, b) => a.localeCompare(b, 'nl'));

// ── beoordelingen ────────────────────────────────────────────────────────────
export const CATEGORIEEN = ['5', '4+', '3-4', '3-'];

export function filterBeoordelingen(items, { categorie = '', functie = '', leverancier = '', periode = '' } = {}) {
  return (items || []).filter((b) => {
    if (categorie && b.categorie !== categorie) return false;
    if (functie && !alsLijst(b.functies).includes(functie)) return false;
    if (leverancier && b.leverancierId !== leverancier) return false;
    if (periode && !(b.datum || '').startsWith(periode)) return false;
    return true;
  });
}

/** Kerncijfers, verdeling per categorie en één kaart per leverancier (laatste beoordeling vooraan). */
export function vatBeoordelingenSamen(items) {
  const kerncijfers = { uitgevoerd: 0, positief: 0, neutraal: 0, negatief: 0 };
  const verdeling = Object.fromEntries(CATEGORIEEN.map((c) => [c, 0]));
  const per = new Map();
  (items || []).forEach((b) => {
    kerncijfers.uitgevoerd += 1;
    if (b.oordeel) kerncijfers[b.oordeel] += 1;
    if (b.categorie in verdeling) verdeling[b.categorie] += 1;
    const sleutel = b.leverancierId || b.leverancier;
    const g = per.get(sleutel) || { id: sleutel, naam: b.leverancier, functies: new Set(), scores: [], laatste: null };
    alsLijst(b.functies).forEach((f) => g.functies.add(f));
    if (b.score !== null && b.score !== undefined) g.scores.push(b.score);
    if (!g.laatste || (b.datum || '') > (g.laatste.datum || '')) g.laatste = b;
    per.set(sleutel, g);
  });
  const leveranciers = [...per.values()].map((g) => {
    const score = g.scores.length ? g.scores.reduce((a, b) => a + b, 0) / g.scores.length : null;
    return {
      id: g.id, naam: g.naam, functies: [...g.functies].sort(), aantal: g.scores.length,
      score: score === null ? null : Math.round(score * 10) / 10,
      oordeel: score === null ? null : score >= 4 ? 'positief' : score >= 3 ? 'neutraal' : 'negatief',
      laatste: g.laatste,
    };
  }).sort((a, b) => (a.naam || '').localeCompare(b.naam || '', 'nl'));
  return {
    kerncijfers,
    verdeling: CATEGORIEEN.map((c) => ({ label: c, aantal: verdeling[c] })),
    leveranciers,
  };
}

// ── overeenkomsten: kalender ─────────────────────────────────────────────────
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Weken (ma–zo) die de maand raken, als ISO-datums; dagen buiten de maand krijgen `buiten`. */
export function maandRaster(jaar, maand /* 0-11 */) {
  const eerste = new Date(jaar, maand, 1);
  const start = new Date(eerste);
  start.setDate(1 - ((eerste.getDay() + 6) % 7));
  const weken = [];
  const d = new Date(start);
  do {
    const week = [];
    for (let i = 0; i < 7; i += 1) {
      week.push({ datum: iso(d), dag: d.getDate(), buiten: d.getMonth() !== maand });
      d.setDate(d.getDate() + 1);
    }
    weken.push(week);
  } while (d.getMonth() === maand);
  return weken;
}

/** Wat er op een dag gebeurt: contracten die dan ingaan of aflopen. Doorlopende contracten
 *  staan niet op elke dag (dat maakt de kalender vol en onrustig). */
export function contractenOpDag(overeenkomsten, datum) {
  const uit = [];
  (overeenkomsten || []).forEach((o) => {
    if (o.start === datum) uit.push({ ...o, soortDag: 'start' });
    else if (o.eind === datum) uit.push({ ...o, soortDag: 'eind' });
  });
  return uit;
}

export const STATUS_TABS = [
  { key: 'geldig', label: 'Geldig' },
  { key: 'concept', label: 'Concept' },
  { key: 'geannuleerd', label: 'Geannuleerd' },
  { key: 'alle', label: 'Alle' },
];

export const filterOpStatus = (items, tab) =>
  tab === 'alle' ? (items || []) : (items || []).filter((o) => o.groep === tab);

export const datumNl = (s) => {
  if (!s) return '—';
  const [j, m, d] = String(s).slice(0, 10).split('-');
  return d && m && j ? `${Number(d)}-${Number(m)}-${j}` : s;
};
